// src/components/Panel.tsx — the one detail surface: right panel on desktop, bottom sheet on mobile.
// Never a modal. As a bottom sheet (under 1100px) it closes on a handle tap or a tap outside it
// (00009 Pattern 3); on desktop it stays open until "Close" so the list beside it stays usable.
import { useEffect, useRef, type ReactNode } from "react";

const SHEET_QUERY = "(max-width: 1099px)";

/** Close a bottom sheet when the operator taps outside it. Desktop side panels ignore outside taps. */
export function useSheetDismiss(onClose: () => void) {
  const ref = useRef<HTMLElement | null>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    function onDown(e: PointerEvent) {
      const el = ref.current;
      if (!el || el.contains(e.target as Node)) return;
      if (typeof window.matchMedia === "function" && !window.matchMedia(SHEET_QUERY).matches) return;
      close.current();
    }
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, []);
  return ref;
}

/** The grab handle: a real button, so tapping it closes the sheet (hidden on desktop). */
export function SheetHandle({ onClose }: { onClose: () => void }) {
  return <button type="button" className="handle" aria-label="Close panel" onClick={onClose} />;
}

export function Panel(props: {
  kicker: string;
  title: ReactNode;
  onClose: () => void;
  closeLabel?: string;
  children: ReactNode;
  label?: string;
}) {
  const ref = useSheetDismiss(props.onClose);
  return (
    <aside ref={ref} className="change-panel" aria-label={props.label ?? props.kicker}>
      <SheetHandle onClose={props.onClose} />
      <div className="panel-head">
        <div>
          <p className="panel-kicker">{props.kicker}</p>
          <h2>{props.title}</h2>
        </div>
        <button type="button" className="btn quiet" onClick={props.onClose}>
          {props.closeLabel ?? "Close"}
        </button>
      </div>
      <div className="panel-body">{props.children}</div>
    </aside>
  );
}
